import { useMemo, useState } from "react";

import {
  AccountPanelHeading,
  AccountTableFrame,
  ColumnHelp,
  SortHeading,
  accountBodyCellClass,
  accountHeadCellClass,
  compareNullableSortValues,
  count,
  navigatorNames,
  percent,
} from "./accountManagementShared";
import type { PipelineCoverage, PipelineCoverageClinic } from "./accountManagementTypes";

type PipelineSortKey =
  | "clinic"
  | "eligibleCarespaceCount"
  | "futureAppointmentRate"
  | "aiAttempt1wRate"
  | "aiAttempt2wRate"
  | "aiAttempt3wRate"
  | "aiAttempt4wRate"
  | "untouched4wRate";

export type PipelineSort = { key: PipelineSortKey; direction: "asc" | "desc" };

const PIPELINE_COLUMNS: Array<{
  key: PipelineSortKey;
  label: string;
  definition: string;
}> = [
  {
    key: "clinic",
    label: "Clinic",
    definition: "The clinic name and the state where it is located.",
  },
  {
    key: "eligibleCarespaceCount",
    label: "Eligible",
    definition: "New referrals that are still open for outreach and are included in this table.",
  },
  {
    key: "futureAppointmentRate",
    label: "Future appointment",
    definition:
      "Eligible referrals that already have an upcoming appointment. The percent is shown above the number of referrals.",
  },
  {
    key: "aiAttempt1wRate",
    label: "AI attempt · 1w",
    definition:
      "Eligible referrals where the AI tried to reach the patient in the last 1 week. The percent is shown above the number of referrals.",
  },
  {
    key: "aiAttempt2wRate",
    label: "AI attempt · 2w",
    definition:
      "Eligible referrals where the AI tried to reach the patient in the last 2 weeks. The percent is shown above the number of referrals.",
  },
  {
    key: "aiAttempt3wRate",
    label: "AI attempt · 3w",
    definition:
      "Eligible referrals where the AI tried to reach the patient in the last 3 weeks. The percent is shown above the number of referrals.",
  },
  {
    key: "aiAttempt4wRate",
    label: "AI attempt · 4w",
    definition:
      "Eligible referrals where the AI tried to reach the patient in the last 4 weeks. The percent is shown above the number of referrals.",
  },
  {
    key: "untouched4wRate",
    label: "Untouched · 4w",
    definition:
      "Eligible referrals with no upcoming appointment and no AI outreach attempt in the last 4 weeks.",
  },
];

function pipelineSortableValue(clinic: PipelineCoverageClinic, key: PipelineSortKey) {
  if (key === "clinic") return clinic.name.toLowerCase();
  return clinic[key];
}

export function sortPipelineClinics(
  clinics: PipelineCoverageClinic[],
  sort: PipelineSort,
): PipelineCoverageClinic[] {
  return [...clinics].sort((left, right) => {
    const comparison = compareNullableSortValues(
      pipelineSortableValue(left, sort.key),
      pipelineSortableValue(right, sort.key),
      sort.direction,
    );
    return comparison || left.name.localeCompare(right.name);
  });
}

function MetricCell({ countValue, rateValue }: { countValue: number; rateValue: number | null }) {
  return (
    <td className={accountBodyCellClass({ numeric: true })}>
      <span className="block font-semibold tabular-nums">{percent(rateValue, 1)}</span>
      <span className="block text-[11px] text-muted-foreground tabular-nums">
        {count(countValue)}
      </span>
    </td>
  );
}

export function PipelineCoveragePanel({
  data,
  clinicFilter,
  navigatorFilter,
}: {
  data: PipelineCoverage;
  clinicFilter: string;
  navigatorFilter: string;
}) {
  const [tableSort, setTableSort] = useState<PipelineSort>({
    key: "untouched4wRate",
    direction: "desc",
  });
  const clinics = useMemo(
    () =>
      data.clinics.filter(
        (clinic) =>
          !clinic.isPortfolioTotal &&
          (clinicFilter === "all" || clinic.id === clinicFilter) &&
          (navigatorFilter === "all" || navigatorNames(clinic.navigator).includes(navigatorFilter)),
      ),
    [clinicFilter, data.clinics, navigatorFilter],
  );
  const tableClinics = useMemo(() => sortPipelineClinics(clinics, tableSort), [clinics, tableSort]);

  function updateTableSort(key: string) {
    setTableSort((current) => ({
      key: key as PipelineSortKey,
      direction: current.key === key && current.direction === "desc" ? "asc" : "desc",
    }));
  }

  return (
    <div className="space-y-5 border-b border-border/70 pb-7">
      <div>
        <AccountPanelHeading>New-referral pipeline health</AccountPanelHeading>
        <p className="mt-1 max-w-4xl text-sm text-muted-foreground">
          Latest GUIDE status is new; latest enrollment status is new referral or unresponsive to
          multiple calls. Archived CareSpaces and AI-marketing opt-outs are excluded.
        </p>
      </div>

      <div>
        <h3 className="text-sm font-semibold">Appointment and AI attempt coverage</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Each cell shows the share of eligible referrals above the referral count. Select any
          column heading to sort the table.
        </p>
      </div>

      <AccountTableFrame aria-label="New-referral pipeline health" minWidthClass="min-w-[1120px]">
        <thead>
          <tr>
            {PIPELINE_COLUMNS.map(({ key, label, definition }, index) => (
              <th
                key={key}
                className={accountHeadCellClass(index === 0)}
                aria-sort={
                  tableSort.key === key
                    ? tableSort.direction === "asc"
                      ? "ascending"
                      : "descending"
                    : "none"
                }
              >
                <span className="inline-flex items-center gap-1">
                  <SortHeading
                    column={key}
                    label={label}
                    sort={tableSort}
                    onSort={updateTableSort}
                  />
                  <ColumnHelp label={label} definition={definition} />
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableClinics.map((clinic) => (
            <tr key={clinic.id} className="group">
              <td className={accountBodyCellClass({ sticky: true })}>
                <span className="block truncate font-semibold" title={clinic.name}>
                  {clinic.name}
                </span>
                <span className="mt-0.5 block text-[11px] text-muted-foreground">
                  {clinic.state || "—"}
                </span>
              </td>
              <td className={accountBodyCellClass({ numeric: true })}>
                {count(clinic.eligibleCarespaceCount)}
              </td>
              <MetricCell
                countValue={clinic.futureAppointmentCount}
                rateValue={clinic.futureAppointmentRate}
              />
              <MetricCell countValue={clinic.aiAttempt1wCount} rateValue={clinic.aiAttempt1wRate} />
              <MetricCell countValue={clinic.aiAttempt2wCount} rateValue={clinic.aiAttempt2wRate} />
              <MetricCell countValue={clinic.aiAttempt3wCount} rateValue={clinic.aiAttempt3wRate} />
              <MetricCell countValue={clinic.aiAttempt4wCount} rateValue={clinic.aiAttempt4wRate} />
              <MetricCell countValue={clinic.untouched4wCount} rateValue={clinic.untouched4wRate} />
            </tr>
          ))}
        </tbody>
      </AccountTableFrame>
      {!clinics.length ? (
        <p className="text-sm text-muted-foreground">No clinics match the active filters.</p>
      ) : null}
    </div>
  );
}
